












































































































import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { BNav, BNavItem, BButton, VBTooltip } from 'bootstrap-vue';
import { get, extend, map, find, pick, startsWith, cloneDeep, last, isEmpty } from 'lodash';
import Draggable from 'vuedraggable';
import { format, parseISO } from 'date-fns';
import LoadingSpinner from '@reciprocity/loading-spinner';
import WorkflowStep from './WorkflowStep';
import WorkflowsZeroState from './WorkflowsZeroState';
import { STEP_DEFAULTS, STEP_CONTENT_PROPS } from '@/state/modules/workflows/workflowsHelpers';
import {
  PEOPLE_QUERY,
  WORKFLOW_GROUPS_QUERY,
  WORKFLOW_STEPS_QUERY,
  WORKFLOW_STEP_CREATE,
  WORKFLOW_STEPS_UPDATE,
  WORKFLOW_GROUP_CREATE,
  WORKFLOW_GROUP_UPDATE,
  OBJECT_DELETE_BY_ID,
} from './WorkflowsGqlHelper';

import { keysToSnake } from '@/utils';
import EditableLabel from '@/components/editable_label/EditableLabel.vue';
import ItemStatusIndicator from '@/components/editable_label/ItemStatusIndicator.vue';

const blankNewGroup = {
  id: 0,
  title: '',
  date: format(Date.now(), 'd/MMM/yyyy h:mm a'),
};

enum SavePart {
  workflows = 'workflows',
  steps = 'steps',
}

export default Vue.extend({
  name: 'WorkflowsPage',
  components: {
    BNav,
    BNavItem,
    WorkflowStep,
    Draggable,
    BButton,
    EditableLabel,
    ItemStatusIndicator,
    LoadingSpinner,
    WorkflowsZeroState,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  data: () => ({
    selectedWorkflowId: -1,
    drag: false,
    lastId: 1,
    lockUI: true,
  }),
  apollo: {
    gqlPeople: {
      query: PEOPLE_QUERY,
      update: result => get(result, 'search.edges'),
      error() {
        this.showLoadError('people');
      },
    },
    gqlWorkflows: {
      query: WORKFLOW_GROUPS_QUERY,
      update: result => get(result, 'search.edges'),
      error() {
        this.lockUI = false;
        this.showLoadError('workflows');
      },
    },
    gqlSteps: {
      query: WORKFLOW_STEPS_QUERY,
      update: result => get(result, 'search.edges'),
      error() {
        this.lockUI = false;
        this.showLoadError('steps');
      },
    },
  },
  computed: {
    ...mapGetters('workflows', ['workflows', 'originalWorkflows', 'modifiedWorkflows', 'steps', 'modifiedSteps']),
    ...mapGetters(['featureFlags']),
    dragOptions() {
      return {
        animation: 300,
        forceFallback: true, // Don't use OS drag image
        draggable: '.workflow-step',
        ghostClass: 'ghost',
        dragClass: 'drag',
        handle: '.drag-icon',
      };
    },
    groupSteps() {
      return this.steps.filter(step => step.groupId === this.selectedWorkflowId);
    },
    people() {
      return map(this.gqlPeople, (o: any) => ({
        id: o.node.dbId,
        label: o.node.name.value,
        value: o.node.email.value,
        role: o.node.userRole.value,
      }));
    },
    isSelectedGroupDirty() {
      return this.groupSteps.some(step => step.dirty);
    },
    isAnyGroupDirty() {
      return this.steps.some(step => step.dirty);
    },
    loading() {
      return this.$apollo.loading;
    },
    anyStepsInSelectedGroup() {
      return !!this.groupSteps.length;
    },
    anyGroups() {
      return !!this.workflows.length;
    },
  },
  watch: {
    workflows: {
      handler(workflows) {
        const selectedExists = workflows.some(a => a.id === this.selectedWorkflowId);
        if (selectedExists) return;
        this.selectedWorkflowId = get(this.workflows, '[0].id', -1);
      },
      deep: true,
      immediate: true,
    },
    gqlWorkflows(gqlWorkflows) {
      this.lockUI = false;
      this.setWorkflowsGqlResponse(gqlWorkflows);
    },
    gqlSteps(gqlSteps) {
      this.lockUI = false;
      this.setStepsGqlResponse(gqlSteps);
    },
  },
  created() {
    this.setPageTitle({ title: 'Workflows' });
    window.onbeforeunload = () => {
      return this.isAnyGroupDirty || undefined;
    };
  },
  methods: {
    ...mapActions({
      setPageTitle: 'setPageTitle',
      showAlert: 'alerts/show',
      showModal: 'modals/show',
    }),
    ...mapActions('workflows', [
      'setWorkflowsGqlResponse',
      'setStepsGqlResponse',
      'createStep',
      'updateStep',
      'createWorkflow',
      'updateWorkflow',
      'deleteStepFromModified',
      'deleteWorkflowFromModified',
    ]),
    selectWorkflow(workflow) {
      this.selectedWorkflowId = workflow.id;
    },
    toggleDragClass(value: boolean) {
      clearTimeout(this.dragTimer);
      if (value) {
        this.drag = true;
      } else {
        // Unset drag with delay, so class for transition is set with delay
        // and item move isn't animated twice:
        this.dragTimer = setTimeout(() => {
          this.drag = false;
        }, 10);
      }
    },
    showLoadError(part): void {
      this.showAlert({
        type: 'error',
        message: `Failed to load ${part}.`,
      });
    },
    async saveWorkflow(workflow): Promise<taskErrorType> {
      let error;
      let existsInDB = !startsWith(workflow.id, 'unsaved');
      const variables = {
        input: {
          title: workflow.title,
        },
      };
      if (existsInDB) {
        variables.id = workflow.id;
      }

      let result = {};
      try {
        result = await this.$apollo.mutate({
          mutation: existsInDB ? WORKFLOW_GROUP_UPDATE : WORKFLOW_GROUP_CREATE,
          variables,
          refetchQueries: [{ query: WORKFLOW_GROUPS_QUERY }],
        });
      } catch (err) {
        error = err;
      }
      let edge = { node: get(result, 'data.result.group') };
      let dbData = {
        id: get(edge, 'node.id'),
        title: get(edge, 'node.title.value'),
        date: format(parseISO(get(edge, 'node.updatedAt.value')), 'd/MMM/yyyy h:mm a'),
      };
      this.updateWorkflow({ id: workflow.id, workflow: dbData });
      if (!existsInDB) {
        this.selectedWorkflowId = dbData.id;
      }

      return { itemId: workflow.id, error: error || '' };
    },
    async saveStep(step): Promise<taskErrorType> {
      let error;
      let existsInDB = !startsWith(step.id, 'unsaved');
      // required input objects must not be null or the complete request is invalid
      // to mitigate this we set them to empty values and let backend validate
      // this guarantees each mutate request to resolve individually even if apollo batches them
      const variables = {
        input: {
          active: step.active,
          title: step.stepTitle || '',
          sourceType: step.sourceType || '',
          triggerType: step.triggerType || '',
          actionTrigger: step.actionTrigger,
          riskThreshold: get(step, 'riskThreshold.id', null),
          objectType: 'Task',
          content: JSON.stringify(keysToSnake(pick(step, STEP_CONTENT_PROPS))),
          workflowGroup: step.groupId,
          order: step.order,
        },
      };

      if (existsInDB) {
        variables.id = step.id;
      }

      let result = {};
      try {
        result = await this.$apollo.mutate({
          mutation: existsInDB ? WORKFLOW_STEPS_UPDATE : WORKFLOW_STEP_CREATE,
          variables,
          refetchQueries: [{ query: WORKFLOW_STEPS_QUERY }],
        });
      } catch (err) {
        error = err;
      }
      if (!error && !existsInDB) {
        let edge = { node: get(result, 'data.result.step') };
        let newId = get(edge, 'node.id');
        this.updateStep({ id: step.id, data: { id: newId } });
      }
      return { itemId: step.id, error: error || '' };
    },
    async save(part: SavePart) {
      let savingSteps = part === SavePart.steps;

      let actions: Promise<taskErrorType>[] = [];

      if (savingSteps) {
        if (this.groupSteps.some(o => !isEmpty(o.validationErrors))) {
          this.showAlert({
            type: 'warning',
            message: 'Errors should be fixed first...',
          });
          return;
        }
        this.modifiedSteps
          .filter(s => s.groupId === this.selectedWorkflowId)
          .forEach(modStep => {
            this.updateStep({ id: modStep.id, data: { serverError: false } });
            actions.push(this.saveStep(modStep));
          });
      } else {
        this.modifiedWorkflows.forEach(modWorkflow => {
          this.updateWorkflow({ id: modWorkflow.id, workflow: extend(modWorkflow, { serverError: false }) });
          actions.push(this.saveWorkflow(modWorkflow));
        });
      }

      this.lockUI = true;
      // Wait for all requests to return:
      let result;
      await Promise.all(actions)
        .then(results => {
          let erroredSteps = this.modifiedSteps.filter(o => get(find(results, { itemId: o.id }), 'error'));
          let erroredWorkflows = this.modifiedWorkflows.filter(o => get(find(results, { itemId: o.id }), 'error'));
          let errored = erroredSteps.concat(erroredWorkflows);

          if (erroredSteps.length) {
            erroredSteps.forEach(step => this.updateStep({ id: step.id, data: { serverError: true } }));
          }

          if (savingSteps || errored.length) {
            this.showAlert({
              type: errored.length ? 'error' : 'success',
              message: errored.length ? 'Unable to save. Please retry.' : 'Workflows updated sucessfuly',
            });
          }
          result = !errored.length;
        })
        .catch(() => {
          this.showAlert({
            type: 'error',
            message: 'Unable to save. Please retry.',
          });
          result = false;
        })
        .finally(() => {
          this.lockUI = false;
        });
      return result;
    },
    async saveSteps() {
      return await this.save(SavePart.steps);
    },
    async saveWorkflows() {
      return await this.save(SavePart.workflows);
    },
    onOrderChanged(data) {
      const oldIndex = data.moved.oldIndex;
      const newIndex = data.moved.newIndex;

      let steps = this.steps.filter(o => o.groupId === this.selectedWorkflowId);
      steps.splice(newIndex, 0, steps.splice(oldIndex, 1)[0]); // move

      // Reindex whole group and push each item to modifiedSteps:
      steps.forEach((step, i) => {
        this.updateStep({ id: step.id, data: { order: i } });
      });
    },
    async addGroupItem() {
      blankNewGroup.id = `unsaved_${this.lastId++}`;
      blankNewGroup.title = this.getNewWorkflowTitle();
      this.createWorkflow(blankNewGroup);
      if (!(await this.saveWorkflows())) {
        this.deleteWorkflowFromModified(blankNewGroup.id);
      } else {
        // Start auto title editing:
        this.updateWorkflow({ id: last(this.workflows).id, workflow: { justCreated: true } });
      }
    },
    addStep() {
      let step = cloneDeep(STEP_DEFAULTS);
      step.justCreated = true;
      step.id = `unsaved_${this.lastId++}`;
      step.internalId = step.id; // Used as vue key, so item doesn't reappear on save
      step.groupId = this.selectedWorkflowId;
      step.order = this.groupSteps.length ? last(this.groupSteps).order + 1 : 1;
      this.createStep(step);
    },
    async deleteStep(id: String) {
      try {
        await this.showModal({
          type: 'GenericConfirm',
          data: {
            title: 'Delete workflow step',
            message: 'Are sure you want to delete this step?',
            okTitle: 'Delete',
          },
        });
      } catch {
        // Canceled by user
        return;
      }
      let existsInDB = !startsWith(id, 'unsaved');
      if (!existsInDB) {
        this.deleteStepFromModified(id);
        return;
      }

      this.lockUI = true;
      let result = {};
      try {
        result = await this.$apollo.mutate({
          mutation: OBJECT_DELETE_BY_ID,
          variables: { ids: [id] },
          refetchQueries: [{ query: WORKFLOW_STEPS_QUERY }],
        });
      } catch (err) {
        this.lockUI = false;
        this.showAlert({
          type: 'error',
          message: 'Unable to delete. Please retry.',
        });
      } finally {
        let ids = get(result, 'data.result.removedIds');
        if (ids) {
          this.deleteStepFromModified(ids[0]);
        }
      }
    },
    removeWorkflowJustCreated(groupId: String) {
      this.updateWorkflow({ id: groupId, workflow: { justCreated: false } });
    },
    anyGroupTitleColisions(groupId: String, newTitle: String) {
      let sameTitles = this.workflows.filter(w => w.id !== groupId && w.title === newTitle);
      return !!sameTitles.length;
    },
    async setWorkflowTitle(groupId: String, newTitle: String, oldTitle: String) {
      newTitle = newTitle.trim();
      if (this.anyGroupTitleColisions(groupId, newTitle)) {
        this.showAlert({
          type: 'error',
          message: `Workflow '${newTitle}' already exists`,
        });
        return;
      }
      this.updateWorkflow({ id: groupId, workflow: { title: newTitle } });
      if (!(await this.saveWorkflows())) {
        this.updateWorkflow({ id: groupId, workflow: { title: oldTitle } });
      }
    },
    async deleteWorkflow(id: String) {
      try {
        await this.showModal({
          type: 'genericDeleteCard',
          data: {
            title: 'Delete workflow',
            message: ['Are sure you want to delete this workflow?', 'Deleted objects can not be restored!'],
            okTitle: 'Delete',
          },
        });
      } catch (error) {
        // Canceled by user
        return;
      }
      this.lockUI = true;
      try {
        let result = await this.$apollo.mutate({
          mutation: OBJECT_DELETE_BY_ID,
          variables: { ids: [id] },
          refetchQueries: [{ query: WORKFLOW_GROUPS_QUERY }, { query: WORKFLOW_STEPS_QUERY }],
        });
        if ((get(result, 'data.result.removedIds') || []).includes(id)) {
          this.deleteWorkflowFromModified(id);
        }
      } catch (err) {
        this.lockUI = false;
        this.showAlert({
          type: 'error',
          message: 'Unable to delete. Please retry.',
        });
      }
    },
    getNewWorkflowTitle() {
      let i = 1;
      let generateName = num => `New Workflow${num === 1 ? '' : ` ${num}`}`;
      while (find(this.originalWorkflows, { title: generateName(i) })) {
        i++;
      }
      return generateName(i);
    },
    groupIndicatorStatus(workflow) {
      if (workflow.invalid) {
        return 'invalid';
      } else if (workflow.dirty) {
        return 'dirty';
      } else {
        return '';
      }
    },
  },
});
